import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../routes/routes';
import type { TokenUsageByEmployeeRow } from '../../apps/apis/claudeCodeDashboard/types';
import { HELP_TEXT } from './agentTelemetryHelp';
import { HelpLabel } from './HelpTooltip';

interface Props {
  data: TokenUsageByEmployeeRow[];
}

const formatCost = (value: number): string => `$${value.toFixed(2)}`;

const formatOptionalNumber = (value: number | undefined): string =>
  value === undefined ? '-' : value.toLocaleString();

const formatRoles = (roles: string[] | undefined): string => {
  if (!roles || roles.length === 0) {
    return '-';
  }
  return roles.join(', ');
};

const formatLastSeen = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString();
};

const EmployeeTokenRanking = ({ data }: Props) => {
  if (data.length === 0) {
    return <div className="text-muted small">No token usage in the selected window.</div>;
  }
  return (
    <Table size="sm" hover responsive>
      <thead>
        <tr>
          <th>
            <HelpLabel description="Employee email or id grouped from agent telemetry.">Employee</HelpLabel>
          </th>
          <th>
            <HelpLabel description={HELP_TEXT.roles}>Roles</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.sessions}>Sessions</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.events}>Events</HelpLabel>
          </th>
          <th>
            <HelpLabel description={HELP_TEXT.lastSeen}>Last seen</HelpLabel>
          </th>
          <th>
            <HelpLabel description={HELP_TEXT.pluginVersion}>Plugin</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.inputTokens}>Input tokens</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.outputTokens}>Output tokens</HelpLabel>
          </th>
          <th className="text-end">
            <HelpLabel description={HELP_TEXT.costUsd}>Cost (USD)</HelpLabel>
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((row) => (
          <tr key={row.employeeId}>
            <td>
              <Link to={ROUTES.agentTelemetry.employeeDetail(row.employeeId)}>{row.employeeId}</Link>
            </td>
            <td className="text-muted small">{formatRoles(row.roles)}</td>
            <td className="text-end">{formatOptionalNumber(row.sessions)}</td>
            <td className="text-end">{formatOptionalNumber(row.events)}</td>
            <td className="text-muted small">{formatLastSeen(row.lastSeenAt)}</td>
            <td className="text-muted small">{row.pluginVersion ?? '-'}</td>
            <td className="text-end">{row.inputTokens.toLocaleString()}</td>
            <td className="text-end">{row.outputTokens.toLocaleString()}</td>
            <td className="text-end">{formatCost(row.totalCostUsd)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default EmployeeTokenRanking;
